import { getModuleRoute, getRouteContext } from "./routeContext"
import { toRouteSlug } from "./routeSlug"

/**
 * Which screens read the API, and which are still drawing the design against
 * sample data.
 *
 * The app is being rebuilt a module at a time, so at any point some screens
 * show real readings and some show a plausible-looking placeholder. That
 * distinction is invisible on the screen itself — sample data is realistic on
 * purpose — and getting it wrong means someone acting on a number that isn't
 * a number. `DataSourceBadge` puts it in the header on every page, and this is
 * the one list it reads.
 *
 * The status here is maintained by hand, and deliberately so: whether a module
 * imports the services layer says only that it *calls* an endpoint, not that
 * the endpoint answers. Several older modules do call APIs that no longer
 * work, and reading the imports would have reported those as connected. Only
 * move a module into the API group once its endpoints are known to serve.
 */

/**
 * "API", not "live": the backend these screens read is the dev server, so the
 * figures are real responses but not production readings. Calling them live
 * would claim more than is true.
 */
export const API_DATA = "api"
export const SAMPLE = "sample"

const HES = { label: "HES", source: API_DATA }
const PRODUCTION_TOOLKIT = { label: "Production Toolkit", source: API_DATA }
const ACCESS_PANEL = { label: "My Access Panel", source: API_DATA }

/** The two report screens are one entry — the sidebar offers one "Reports". */
const REPORTS = { label: "Reports", source: SAMPLE }

/**
 * A module wired tab by tab.
 *
 * Operations and Dashboard each have some tabs on the backend and some still
 * on placeholders, and the tab is in the URL — so the honest answer is per
 * route, not per module. A module-level answer would be wrong on six routes
 * to be right on two, or the reverse.
 *
 * The wired tabs get their own name in the panel's list, because "Operations"
 * appearing under both headings would say less than nothing.
 *
 * CMM is not listed here: its tabs are local state, not routes, so there is no
 * path to tell them apart by.
 */
const TAB_SOURCES = {
  operations: {
    // Every card reads `operation-monitoring`, plus the Dashboard service for
    // commissioning steps and the older report behind Max Current.
    dashboard: { label: "Operations › Dashboard", source: API_DATA },
    "consumer-info": { label: "Operations › Consumer Info.", source: API_DATA },
    "recharge-reversal": { label: "Operations › Recharge Reversal", source: API_DATA }
  },
  dashboard: {
    operations: { label: "Dashboard › Operations", source: API_DATA },
    alerts: { label: "Dashboard › Alerts", source: API_DATA },
    communication: { label: "Dashboard › Communication", source: API_DATA },
    consumption: { label: "Dashboard › Consumption", source: API_DATA },
    // Both charts read the backend; the three KPI tiles above them do not,
    // because this tag has no `kpis` endpoint. The tab says so on screen.
    "meter-health": { label: "Dashboard › Meter Health", source: API_DATA }
  }
}

/**
 * Keyed by the route slug `MODULE_MAP` uses, so the two stay comparable.
 *
 * Several slugs share one entry where they are one thing to the reader: the
 * Production Toolkit is eight routes but one module in the sidebar, and naming
 * each of its jigs separately would bury the four names that matter.
 */
export const MODULE_SOURCES = {
  // -- reading the API ---------------------------------------------------------
  hes: HES,
  productiontoolkit: PRODUCTION_TOOLKIT,
  station40: PRODUCTION_TOOLKIT,
  station60: PRODUCTION_TOOLKIT,
  operationstation60: PRODUCTION_TOOLKIT,
  basejig: PRODUCTION_TOOLKIT,
  commsjig: PRODUCTION_TOOLKIT,
  productiondashboard: PRODUCTION_TOOLKIT,
  factoryfileupload: PRODUCTION_TOOLKIT,
  billing: { label: "Billing", source: API_DATA },
  caq: { label: "CAQ", source: API_DATA },
  // Every tab reads the backend, Reconciliation History included — it was the
  // last placeholder under an "API data" badge and now runs on
  // `gas-command-request/list/`.
  cmm: { label: "CMM", source: API_DATA },

  // -- part wired, part not: see TAB_SOURCES ----------------------------------
  dashboard: { label: "Dashboard", source: SAMPLE },
  operations: { label: "Operations", source: SAMPLE },

  // -- design built, endpoints still to come ----------------------------------
  vee: { label: "VEE", source: SAMPLE },
  reports: REPORTS,
  analytics: REPORTS
}

/** The pages that aren't project modules, keyed by their path. */
export const PAGE_SOURCES = {
  // The feed is the report queue — GET /hes/api/v1/reports/downloads.
  "/notifications": { label: "Notifications", source: API_DATA },
  "/user-access-panel": ACCESS_PANEL,
  "/project-access-panel": ACCESS_PANEL,
  // Never carries the badge — it renders outside the app shell — but it is
  // part of what's connected, so it belongs in the list.
  "/login": { label: "Login / Signup", source: API_DATA }
}

/**
 * What the current route is showing.
 *
 * A route that isn't listed is reported as sample with no name. That is the
 * safe direction to be wrong in: understating what's connected sends someone
 * to check a number that was fine, while overstating it lets them act on one
 * that wasn't. The unnamed screens are the older modules nobody has confirmed
 * either way — add them here as they're checked.
 */
export const resolveDataSource = (pathname = "") => {
  const path = String(pathname).replace(/\/+$/, "") || "/"

  const page = PAGE_SOURCES[path]
  if (page) return page

  const { module } = getRouteContext(path)
  if (!module) return { label: null, source: SAMPLE }

  const slug = toRouteSlug(module)

  // A wired tab of a part-wired module answers for itself. `getModuleRoute`
  // is what knows where the tab sits, since the legacy routes carry the
  // vertical ahead of the project and push every segment along one.
  const tab = TAB_SOURCES[slug]?.[getModuleRoute(path).segments[0]]
  if (tab) return tab

  return MODULE_SOURCES[slug] ?? { label: null, source: SAMPLE }
}

/** Every entry, split into the two groups, each alphabetical. */
export const dataSourceGroups = () => {
  // Entries are shared across slugs on purpose; the reader wants each name once.
  const byLabel = new Map()
  Object.values(MODULE_SOURCES)
    .concat(Object.values(PAGE_SOURCES))
    .concat(Object.values(TAB_SOURCES).flatMap((tabs) => Object.values(tabs)))
    .forEach((entry) => byLabel.set(entry.label, entry))

  const entries = [...byLabel.values()].sort((a, b) => a.label.localeCompare(b.label))

  return {
    api: entries.filter((entry) => entry.source === API_DATA),
    sample: entries.filter((entry) => entry.source === SAMPLE)
  }
}

/** "4 of 12 connected" — how far through the rebuild the backend is. */
export const dataSourceProgress = () => {
  const { api, sample } = dataSourceGroups()
  const total = api.length + sample.length

  return { connected: api.length, total, percent: total ? (api.length / total) * 100 : 0 }
}
